// src/components/common/audio-player/session/AudioSession.ts

type Listener = () => void;

class AudioSession {
  private audio: HTMLAudioElement | null = null;
  private ownerId: string | null = null;
  private listeners = new Set<Listener>();

  /* ---------------- attach ---------------- */

  // AudioSession.ts
  attach(audio: HTMLAudioElement, ownerId: string) {
    // ✅ unbind from previous audio first
    if (this.audio && this.audio !== audio) {
      this.unbindEvents();
    }

    if (this.ownerId && this.ownerId !== ownerId) {
      this.audio?.pause();
    }

    this.audio = audio;
    this.ownerId = ownerId;

    this.bindEvents();
    this.emit();
  }


  detach(ownerId: string) {
    if (this.ownerId === ownerId) {
      this.unbindEvents();
      this.audio = null;
      this.ownerId = null;
      this.emit();
    }
  }

  getAudio() {
    return this.audio;
  }

  /* ---------------- controls ---------------- */

  play = () => {
    this.audio?.play().catch(() => { });
  };

  pause = () => {
    this.audio?.pause();
  };

  toggle = () => {
    if (!this.audio) return;
    this.audio.paused ? this.play() : this.pause();
  };

  seek = (t: number) => {
    if (!this.audio) return;
    const d = this.audio.duration || Infinity;
    this.audio.currentTime = Math.max(0, Math.min(d, t));
  };

  setRate = (r: number) => {
    if (!this.audio) return;
    this.audio.playbackRate = r;
  };

  /* ---------------- events sync ---------------- */

  private onPlay = () => this.emit();
  private onPause = () => this.emit();
  private onTime = () => this.emit();

  private bindEvents() {
    if (!this.audio) return;
    this.audio.addEventListener("play", this.onPlay);
    this.audio.addEventListener("pause", this.onPause);
    this.audio.addEventListener("timeupdate", this.onTime);
  }

  private unbindEvents() {
    if (!this.audio) return;
    this.audio.removeEventListener("play", this.onPlay);
    this.audio.removeEventListener("pause", this.onPause);
    this.audio.removeEventListener("timeupdate", this.onTime);
  }

  /* ---------------- subscription ---------------- */

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    this.listeners.forEach((l) => l());
  }
}

export const audioSession = new AudioSession();
